// What the service worker and the app hand each other while the app is
// closed. The service worker can't reach the database - OPFS's sync access
// handles only exist in dedicated workers - and never holds the key: it only
// carries ciphertext between GitHub and here, and the database's worker takes
// it in, or hands it out, the next time the app runs.
//
//   state     the repo config, and the remote sha of every note the
//             database already has, so a prefetch skips those
//   blobs     git blob sha -> ciphertext fetched ahead of the next sync
//   outbox    note id -> ciphertext the app left for the service worker
//   receipts  note id -> what the service worker pushed from the outbox
import type { RepoConfig } from './github';

export const PUSH_TAG = 'notes-push';
export const PULL_TAG = 'notes-pull';

export interface OutboxEntry {
  id: string;
  // null deletes the file
  text: string | null;
  baseSha: string | null;
  updatedAt: string;
}

export type Receipt =
  | { id: string, sha: string, updatedAt: string }
  | { id: string, removed: true };

type StoreName = 'state' | 'blobs' | 'outbox' | 'receipts';

export interface Handoff {
  config(): Promise<RepoConfig | null>;
  setConfig(config: RepoConfig): Promise<void>;
  known(): Promise<Record<string, string>>;
  setKnown(known: Record<string, string>): Promise<void>;
  blob(sha: string): Promise<string | null>;
  blobShas(): Promise<string[]>;
  putBlob(sha: string, text: string): Promise<void>;
  // drops every blob but these
  keepBlobs(shas: string[]): Promise<void>;
  outbox(): Promise<OutboxEntry[]>;
  setOutbox(entries: OutboxEntry[]): Promise<void>;
  // records a push and takes its entry out of the outbox, in one go
  settle(receipt: Receipt): Promise<void>;
  receipts(): Promise<Receipt[]>;
  dropReceipts(ids: string[]): Promise<void>;
}

// One sync at a time across the app and the service worker: both pushing the
// same note would turn the second push into a conflict that isn't one.
export const exclusively = <T>(fn: () => Promise<T>): Promise<T> =>
  navigator.locks.request('notes-sync', fn);

const open = (name: string) =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(name, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      db.createObjectStore('state');
      db.createObjectStore('blobs');
      db.createObjectStore('outbox', { keyPath: 'id' });
      db.createObjectStore('receipts', { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const done = (tx: IDBTransaction) =>
  new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });

export const openHandoff = (name = 'notes-background'): Handoff => {
  // `op` queues its requests; the result is read once the transaction is done
  const run = async <T>(
    stores: StoreName[],
    mode: IDBTransactionMode,
    op: (store: (name: StoreName) => IDBObjectStore) => IDBRequest<T> | void,
  ): Promise<T | undefined> => {
    const db = await open(name);
    try {
      const tx = db.transaction(stores, mode);
      const req = op(n => tx.objectStore(n));
      await done(tx);
      return req?.result;
    } finally {
      db.close();
    }
  };

  const get = <T>(store: StoreName, key: string) =>
    run<T | undefined>([store], 'readonly', s => s(store).get(key));

  const getAll = <T>(store: StoreName) =>
    run<T[]>([store], 'readonly', s => s(store).getAll())
      .then(all => all ?? []);

  const put = (store: StoreName, value: unknown, key?: string) =>
    run([store], 'readwrite', s => {
      s(store).put(value, key);
    }).then(() => undefined);

  const blobShas = () =>
    run<IDBValidKey[]>(['blobs'], 'readonly', s => s('blobs').getAllKeys())
      .then(keys => (keys ?? []).map(String));

  return {
    config: () => get<RepoConfig>('state', 'config').then(c => c ?? null),
    setConfig: config => put('state', config, 'config'),
    known: () =>
      get<Record<string, string>>('state', 'known').then(k => k ?? {}),
    setKnown: known => put('state', known, 'known'),
    blob: sha => get<string>('blobs', sha).then(b => b ?? null),
    blobShas,
    putBlob: (sha, text) => put('blobs', text, sha),
    keepBlobs: async shas => {
      const keep = new Set(shas);
      const all = await blobShas();
      await run(['blobs'], 'readwrite', s => {
        all.filter(sha => !keep.has(sha)).forEach(sha =>
          s('blobs').delete(sha)
        );
      });
    },
    outbox: () => getAll<OutboxEntry>('outbox'),
    setOutbox: entries =>
      run(['outbox'], 'readwrite', s => {
        s('outbox').clear();
        entries.forEach(e => s('outbox').put(e));
      }).then(() => undefined),
    settle: receipt =>
      run(['outbox', 'receipts'], 'readwrite', s => {
        s('receipts').put(receipt);
        s('outbox').delete(receipt.id);
      }).then(() => undefined),
    receipts: () => getAll<Receipt>('receipts'),
    dropReceipts: ids =>
      run(['receipts'], 'readwrite', s => {
        ids.forEach(id => s('receipts').delete(id));
      }).then(() => undefined),
  };
};
